<template>
  <div class="settings-profile" data-testid="settings-profile">
    <!-- Avatar -->
    <div class="sp-avatar-row">
      <div class="sp-avatar" :aria-label="`${auth.user.value?.name} avatar`">
        {{ initial }}
      </div>
      <div class="sp-identity">
        <p class="sp-name" data-testid="settings-profile-name">
          {{ auth.user.value?.name }}
        </p>
        <p
          v-if="profileData.profile.value?.created_at"
          class="sp-member-since"
          data-testid="settings-profile-member-since"
        >
          {{ STRINGS.templates.memberSince(formattedDate) }}
        </p>
      </div>
    </div>

    <!-- Stats card -->
    <div class="sp-stats" data-testid="settings-profile-stats">
      <p class="sp-stats-label">
        {{ STRINGS.profile.stats.totalPoints }}
      </p>
      <p class="sp-stats-value" data-testid="settings-profile-total-points">
        {{ profileData.profile.value?.total_points ?? 0 }}
      </p>
    </div>

    <!-- Display name section: view / edit / success states -->
    <div class="sp-actions">
      <PrimeButton
        v-if="mode === 'view'"
        variant="secondary"
        class="w-full min-h-12 touch-manipulation"
        :aria-label="STRINGS.aria.labels.changeDisplayName"
        data-testid="settings-change-name-button"
        @click="mode = 'edit-name'"
      >
        {{ STRINGS.profile.buttons.changeName }}
      </PrimeButton>

      <!-- Inline name edit form -->
      <form
        v-if="mode === 'edit-name'"
        class="sp-form"
        @submit.prevent="handleNameSubmit"
        novalidate
      >
        <div class="flex flex-col gap-1.5">
          <label for="settings-new-name" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.profile.labels.newName }}
          </label>
          <div class="relative">
            <InputText
              id="settings-new-name"
              ref="nameInput"
              v-model="newName"
              class="sp-input min-h-12 touch-manipulation w-full"
              :class="saveSuccess ? 'border-energy-success bg-energy-success/5' : ''"
              :placeholder="STRINGS.profile.placeholders.newName"
              autocomplete="username"
              :aria-invalid="!!nameError"
              data-testid="settings-profile-name-input"
              @keyup.enter="handleNameSubmit"
            />
            <span
              v-if="saveSuccess"
              class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-energy-success text-white flex items-center justify-center border-2 border-arena-void animate-badge-pop"
              aria-hidden="true"
              ><AppIcon name="checkmark" :size="10" color="white"
            /></span>
          </div>
          <p
            v-if="saveSuccess"
            class="text-xs text-energy-success flex items-center gap-1 m-0"
            role="status"
            aria-live="polite"
          >
            <AppIcon
              name="checkmark"
              :size="14"
              color="var(--color-energy-success)"
              aria-hidden="true"
            />
            {{ STRINGS.profile.success.nameChanged }}
          </p>
          <p v-else class="text-xs text-arena-text-muted m-0">
            {{ STRINGS.profile.hints.nameRules }}
          </p>
        </div>

        <!-- Error -->
        <p
          v-if="nameError"
          class="text-sm text-error-500 text-center m-0"
          role="alert"
          aria-live="polite"
          data-testid="settings-profile-name-error"
        >
          {{ nameError }}
        </p>

        <div class="flex gap-3">
          <PrimeButton
            class="flex-1 min-h-12 touch-manipulation"
            :disabled="!canSaveName"
            :aria-label="STRINGS.aria.labels.saveNewName"
            data-testid="settings-profile-save-name-button"
            @click="handleNameSubmit"
          >
            <template v-if="profileData.isLoading.value">
              {{ STRINGS.profile.buttons.save }}<LoadingDots />
            </template>
            <template v-else>
              {{ STRINGS.profile.buttons.save }}
            </template>
          </PrimeButton>
          <PrimeButton
            variant="secondary"
            class="flex-1 min-h-12 touch-manipulation"
            :aria-label="STRINGS.aria.labels.cancelNameChange"
            data-testid="settings-profile-cancel-name-button"
            @click="cancelNameEdit"
          >
            {{ STRINGS.profile.buttons.cancel }}
          </PrimeButton>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from '../ui/PrimeButton.vue';
import LoadingDots from '../ui/LoadingDots.vue';
import AppIcon from '../ui/icons/AppIcon.vue';
import { useAuth } from '@/composables/useAuth';
import { useProfile } from '@/composables/useProfile';
import { isAuthError } from '@/types/auth';
import { UI_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';

const auth = useAuth();
const profileData = useProfile();

const mode = ref<'view' | 'edit-name'>('view');

const newName = ref('');
const nameError = ref('');
const nameInput = ref();
const saveSuccess = ref(false);

const initial = computed(() => {
  const name = auth.user.value?.name ?? '';
  return name.charAt(0).toUpperCase();
});

const formattedDate = computed(() => {
  const raw = profileData.profile.value?.created_at;
  if (!raw) return '';
  const d = new Date(raw);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
});

const canSaveName = computed(() => {
  const trimmed = newName.value.trim();
  return (
    trimmed.length >= UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH &&
    trimmed.length <= UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH &&
    UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed) &&
    !profileData.isLoading.value &&
    !saveSuccess.value
  );
});

async function handleNameSubmit(): Promise<void> {
  if (!canSaveName.value) return;

  nameError.value = '';
  profileData.clearError();

  try {
    await profileData.updateName(newName.value);
    newName.value = '';
    saveSuccess.value = true;
    setTimeout(() => {
      saveSuccess.value = false;
      mode.value = 'view';
    }, 2000);
  } catch (err: unknown) {
    if (isAuthError(err)) {
      nameError.value = err.type === 'name_taken' ? STRINGS.profile.errors.nameTaken : err.message;
    } else {
      nameError.value = STRINGS.auth.errors.network;
    }
  }
}

function cancelNameEdit(): void {
  mode.value = 'view';
  newName.value = '';
  nameError.value = '';
  profileData.clearError();
}

onMounted(async () => {
  const user = auth.user.value;
  if (user) {
    try {
      await profileData.fetchProfile(user.id);
    } catch {
      // Error displayed via profileData.error
    }
  }
});
</script>

<style scoped>
.settings-profile {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.sp-avatar-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sp-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: var(--font-display);
  background: var(--color-primary-500);
  border: 2px solid var(--color-energy-primary);
  box-shadow: 0 0 16px rgba(201, 168, 76, 0.3);
  flex-shrink: 0;
}

.sp-identity {
  min-width: 0;
}

.sp-name {
  font-family: var(--font-mono);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sp-member-since {
  font-size: 0.75rem;
  color: var(--color-arena-text-muted);
  margin: 0.125rem 0 0;
}

.sp-stats {
  background: var(--color-arena-void);
  border: 1px solid var(--color-arena-border);
  border-radius: 0.75rem;
  padding: 1rem;
}

.sp-stats-label {
  font-size: 0.6875rem;
  color: var(--color-arena-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin: 0 0 0.25rem;
}

.sp-stats-value {
  font-family: var(--font-mono);
  font-size: 1.5rem;
  color: var(--color-primary-500);
  font-weight: 700;
  margin: 0;
}

.sp-actions {
  display: flex;
  flex-direction: column;
}

.sp-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--color-arena-border);
  background: rgba(26, 22, 18, 0.8);
}

.sp-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: border-color 150ms ease-out;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
}

.sp-input:focus {
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px var(--color-primary-500);
}

.sp-input:focus-visible {
  outline: 3px solid var(--color-primary-500);
  outline-offset: 2px;
}

.sp-input::placeholder {
  color: var(--color-arena-text-muted);
}

@keyframes badge-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  70% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-badge-pop {
  animation: badge-pop 0.3s var(--ease-bounce, cubic-bezier(0.34, 1.56, 0.64, 1));
}

@media (prefers-reduced-motion: reduce) {
  .animate-badge-pop {
    animation: none;
  }
}
</style>
